import React, { useMemo, useCallback, useState, useEffect } from 'react';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  Button,
  Typography,
  Radio,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { isEmpty, find, isEqual, concat, isFunction, findIndex } from 'lodash';
import PropTypes from 'prop-types';

import { t } from '~/helpers';

import { Search, CardMobile } from './components';
import * as S from './styles';

export default function Tabela({
  title,
  cells,
  data,
  height,
  size,
  checkbox,
  radio,
  loading,
  onCallback,
  unique,
  paginate,
  labelButton,
  search,
  showCount,
  noButton,
  clearCheckeds,
  showPagination,
  onCallbackChanged,
  maxHeight,
  onRow,
  isResponsive,
}) {
  const [heading, setHeading] = useState([]);
  const [selecteds, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);

  const [activeFilter, setActiveFilter] = useState('primary');

  const totalPages = useMemo(
    () =>
      Math.ceil(
        data.filter((item) =>
          !isEmpty(query)
            ? item[query.column].toString().toLowerCase().indexOf(query.text) >
              -1
            : true
        ).length / paginate
      ),
    [data, paginate, query]
  );

  const countPage = useMemo(() => Number(page * paginate - paginate), [
    page,
    paginate,
  ]);

  const delimiter = useMemo(() => Number(countPage + paginate), [
    countPage,
    paginate,
  ]);

  const indeterminate = useMemo(
    () => !isEmpty(selecteds) && selecteds.length < data.length,
    [selecteds, data]
  );

  const checked = useMemo(
    () => !isEmpty(data) && selecteds.length === data.length,
    [selecteds, data]
  );

  const isRow = useMemo(() => !checkbox && !radio && !onRow, [
    checkbox,
    radio,
    onRow,
  ]);

  const handleCheckItem = useCallback(
    (isChecked, item) => {
      if (checkbox) {
        if (isChecked) {
          setSelected((select) => [...select, item]);
          return;
        }

        setSelected((it) => it.filter((select) => !isEqual(select, item)));
      } else {
        if (isChecked) {
          setSelected([item]);
          return;
        }

        setSelected([]);
      }
    },
    [radio, checkbox]
  );

  const handleCheckItemBody = useCallback(
    (isCheck, position, cell, configure) => {
      const key = findIndex(data, position);

      onCallbackChanged(
        data.map((state, index) => {
          if (index !== key) return state;
          return { ...state, [cell]: configure ? configure[isCheck] : isCheck };
        })
      );
    },
    [data]
  );

  const renderHeader = useMemo(
    () =>
      heading.map((cell, index) => (
        <TableCell
          key={`cell-index-head-${index}`}
          align={cell.align || 'left'}
        >
          {cell.label}
        </TableCell>
      )),
    [heading]
  );

  const renderBody = useMemo(
    () =>
      concat([], data)
        .filter((item) =>
          !isEmpty(query)
            ? item[query.column]
                .toString()
                .toLowerCase()
                .indexOf(query.text.toString().toLowerCase()) > -1
            : true
        )
        .slice(countPage, delimiter)
        .map((item, index) => (
          <TableRow
            hover
            tabIndex={-1}
            key={`table-row-body-${index}`}
            selected={!!find(selecteds, (select) => select === item)}
            onClick={() => {
              if (isRow) {
                handleCheckItem(true, item);
              } else {
                handleCheckItem(true, item);
                onRow(item);
              }
            }}
          >
            {(checkbox || radio) && (
              <TableCell padding="checkbox">
                {checkbox ? (
                  <Checkbox
                    checked={!!find(selecteds, (select) => select === item)}
                    inputProps={{ 'aria-labelledby': index }}
                    onClick={({ target }) =>
                      handleCheckItem(target.checked, item)
                    }
                  />
                ) : (
                  <Radio
                    checked={!!find(selecteds, (select) => select === item)}
                    onChange={({ target }) =>
                      handleCheckItem(target.checked, item)
                    }
                  />
                )}
              </TableCell>
            )}
            {heading.map((cell, indexKey) => (
              <TableCell
                key={`table-cell-body-${indexKey}`}
                align={cell.align || 'left'}
                style={cell.style ? cell.style(item) : undefined}
                padding={cell.type ? 'checkbox' : 'default'}
              >
                {!cell.type &&
                  (cell.format ? cell.format(item[cell.id]) : item[cell.id])}
                {cell.type && cell.type === 'checkbox' && (
                  <Checkbox
                    checked={
                      cell.configure
                        ? cell.configure.true === item[cell.id]
                        : item[cell.id]
                    }
                    onChange={({ target }) =>
                      handleCheckItemBody(
                        target.checked,
                        item,
                        cell.id,
                        cell.configure
                      )
                    }
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        )),
    [
      heading,
      data,
      checkbox,
      radio,
      selecteds,
      unique,
      paginate,
      countPage,
      delimiter,
      query,
    ]
  );

  const handleToggleCheckedItems = useCallback(() => {
    if (isEmpty(selecteds) || selecteds.length < data.length) {
      setSelected(data);
      return;
    }

    setSelected([]);
  }, [selecteds, data]);

  const handleSubmit = useCallback(() => {
    onCallback(selecteds);
    setSelected([]);
  }, [selecteds]);

  const handlePagination = useCallback((pg) => {
    setPage(pg);
  }, []);

  const handleChangeSearch = useCallback((value) => {
    setQuery(value);
    setActiveFilter(isEmpty(value.text) ? 'primary' : 'secondary');
  }, []);

  useEffect(() => {
    if (totalPages < page) {
      setPage(totalPages || 1);
    }
  }, [data, page, totalPages]);

  useEffect(() => {
    if (!isEmpty(cells)) setHeading(cells);
  }, [cells]);

  useEffect(() => {
    if (noButton) onCallback(selecteds);
  }, [noButton, selecteds]);

  useEffect(() => {
    if (clearCheckeds) setSelected([]);
  }, [clearCheckeds]);

  window.addEventListener('resize', () => {
    // check is mobile
    if (window.innerWidth <= 500) setIsMobile(true);
    else setIsMobile(false);
  });

  if (isMobile && isResponsive)
    return (
      <S.Content>
        {loading && (
          <S.Loading>
            <CircularProgress />
          </S.Loading>
        )}

        {title && <Typography>{title}</Typography>}

        {data.map((item) => (
          <CardMobile data={item} cells={cells} />
        ))}
      </S.Content>
    );

  return (
    <S.WrapperTabela height={height}>
      {loading && (
        <S.Loading>
          <CircularProgress />
        </S.Loading>
      )}

      {(title || search || (isFunction(onCallback) && !noButton)) && (
        <S.WrapperTitle>
          {title && <Typography>{title}</Typography>}

          {(search || (isFunction(onCallback) && !noButton)) && (
            <S.WrapperSearch>
              {search && (
                <Search
                  options={heading}
                  onSearch={handleChangeSearch}
                  activeFilter={activeFilter}
                />
              )}

              {isFunction(onCallback) && !noButton && (
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={handleSubmit}
                  disabled={checkbox || radio ? isEmpty(selecteds) : false}
                >
                  {t(labelButton)}
                </Button>
              )}
            </S.WrapperSearch>
          )}
        </S.WrapperTitle>
      )}

      <TableContainer style={{ maxHeight: `${maxHeight}px` }}>
        <Table stickyHeader aria-label="sticky table" size={size}>
          <TableHead>
            <TableRow>
              {(checkbox || radio) && (
                <TableCell padding="checkbox">
                  {checkbox && (
                    <Checkbox
                      indeterminate={indeterminate}
                      checked={checked}
                      onChange={handleToggleCheckedItems}
                      inputProps={{ 'aria-label': 'select all desserts' }}
                    />
                  )}
                </TableCell>
              )}
              {renderHeader}
            </TableRow>
          </TableHead>
          <TableBody>{renderBody}</TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <S.Footer>
          <Typography>
            {showCount && (
              <>
                <span>{t('s.geral.total')}:</span> {data.length}
              </>
            )}
          </Typography>

          {!isEmpty(data) && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, pg) => handlePagination(pg)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          )}
        </S.Footer>
      )}
    </S.WrapperTabela>
  );
}

Tabela.propTypes = {
  title: PropTypes.string.isRequired,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      style: PropTypes.func,
      format: PropTypes.func,
      type: PropTypes.string,
      configure: PropTypes.objectOf(
        PropTypes.shape({
          true: PropTypes.string,
          false: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  height: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  checkbox: PropTypes.bool,
  radio: PropTypes.bool,
  loading: PropTypes.bool,
  onCallback: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  unique: PropTypes.string.isRequired,
  paginate: PropTypes.number,
  labelButton: PropTypes.string,
  search: PropTypes.bool,
  showCount: PropTypes.bool,
  noButton: PropTypes.bool,
  clearCheckeds: PropTypes.bool,
  showPagination: PropTypes.bool,
  onCallbackChanged: PropTypes.func,
  maxHeight: PropTypes.number,
  onRow: PropTypes.func,
  isResponsive: PropTypes.bool,
};

Tabela.defaultProps = {
  height: '100%',
  size: 'medium',
  checkbox: false,
  radio: false,
  loading: false,
  onCallback: false,
  paginate: 5,
  labelButton: 's.geral.botaoconfirmar',
  search: false,
  showCount: false,
  noButton: false,
  clearCheckeds: false,
  showPagination: true,
  onCallbackChanged: () => undefined,
  maxHeight: 440,
  onRow: () => undefined,
  isResponsive: false,
};
